import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  myTeamMonthlyTardinessSummaryService,
  type MyTeamMonthlyTardinessSummary,
} from '@/api/domains/attendance/myTeam';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useUI } from '@/hooks/ui/useUI';


export interface UseMyTeamMonthlyTardinessSummaryResult {
  summary: MyTeamMonthlyTardinessSummary | null;
  isLoading: boolean;
}

// Data hook for the "Mi equipo" dashboard "Resumen del mes" — "Atrasos" card
// (ASIS-5410 / REXASIS-009). Recalculates whenever `companyId`/`monthlyCutId` change —
// i.e. whenever the Empresa/Año/Mes/Descripción filters of "Resumen del mes" resolve to
// a different company or cutoff. Unlike the "hoy" cards, `monthlyCutId` can legitimately
// be null (no cutoffs available for the selected company, per ASIS-5414) — in that case
// this hook skips the fetch entirely instead of calling the backend with an invalid id.
export function useMyTeamMonthlyTardinessSummary(
  companyId: number | null,
  monthlyCutId: number | null,
): UseMyTeamMonthlyTardinessSummaryResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();

  const [summary, setSummary] = useState<MyTeamMonthlyTardinessSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (companyId == null || monthlyCutId == null) {
      setSummary(null);
      setIsLoading(false);
      return;
    }

    const signal = createSignal();
    setIsLoading(true);

    void (async () => {
      const response = await myTeamMonthlyTardinessSummaryService.getSummary(
        { cutoffCompanyId: companyId, monthlyCutId },
        signal,
      );
      if (response.error?.code === 'ABORTED') return;
      setIsLoading(false);

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.myTeam.monthlyTardinessSummary.loadError'));
        return;
      }

      setSummary(response.data);
    })();
  }, [companyId, monthlyCutId, createSignal, showApiError, translate]);

  return { summary, isLoading };
}

export default useMyTeamMonthlyTardinessSummary;
